import type { GlAccount, SalesSplit } from '../types';

/**
 * The client's chart, as the G/L Mappings screen uses it (docs/client-chart-and-sales-split.md §1–§2).
 * The server is the authority on every rule here (`GlChartRules`); these are the same rules said early, so the
 * bookkeeper sees a problem beside the field instead of after Save.
 */

/** Xero's account types, the same list as `GlAccountTypes.All` on the server. */
export const GL_ACCOUNT_TYPES = [
  'REVENUE', 'SALES', 'OTHERINCOME', 'DIRECTCOSTS', 'EXPENSE', 'OVERHEADS', 'DEPRECIATN',
  'BANK', 'CURRENT', 'INVENTORY', 'PREPAYMENT', 'FIXED', 'NONCURRENT',
  'CURRLIAB', 'LIABILITY', 'TERMLIAB', 'EQUITY',
] as const;

const sameCode = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export function findAccount(accounts: readonly GlAccount[], code: string): GlAccount | undefined {
  return code.trim() ? accounts.find((a) => sameCode(a.code, code)) : undefined;
}

/** What a person sees for an account: their own name for it, else its name. */
export const displayName = (a: GlAccount): string => a.nameOverride?.trim() || a.name?.trim() || '';

/**
 * The chart in Xero's order (ruled 2026-10-29, docs/client-journal-template.md §B4): by code, numbers in number order,
 * then by name. A copy; the stored order is not the screen's.
 */
export function sortedChart(accounts: readonly GlAccount[]): GlAccount[] {
  return [...accounts].sort(
    (x, y) => x.code.localeCompare(y.code, undefined, { numeric: true, sensitivity: 'base' })
      || displayName(x).localeCompare(displayName(y), undefined, { sensitivity: 'base' }),
  );
}

/** The accounts a mapping row may pick: active ones, in code order. Archived accounts are hidden, never deleted. */
export function pickable(accounts: readonly GlAccount[]): GlAccount[] {
  return accounts.filter((a) => a.status !== 'ARCHIVED').sort((x, y) => x.code.localeCompare(y.code, undefined, { numeric: true }));
}

/**
 * The chart to save: the chart as edited, plus every code a mapping row names that is not in it yet, added as `typed`
 * with no name (§2: "type a code" for an account not in the chart adds it).
 */
export function chartWithMappedCodes(accounts: readonly GlAccount[], codes: Iterable<string>): GlAccount[] {
  const result = [...accounts];
  for (const raw of codes) {
    const code = raw.trim();
    if (code && !findAccount(result, code)) result.push({ code, status: 'ACTIVE', source: 'typed' });
  }
  return result;
}

/** Why a new account cannot be added, or null. The same two refusals the server makes (`GlChartRules`). */
export function newAccountProblem(accounts: readonly GlAccount[], code: string): 'codeRequired' | 'duplicate' | null {
  if (!code.trim()) return 'codeRequired';
  return findAccount(accounts, code) ? 'duplicate' : null;
}

/**
 * A key for a client-defined mapping row: `custom-` plus a slug of its label, made unique against the keys in use.
 * The key is stable once saved; the label is what a person reads and may change.
 */
export function customRoleKey(label: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  const slug = label
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'row';
  let key = `custom-${slug}`;
  for (let n = 2; used.has(key); n++) key = `custom-${slug}-${n}`;
  return key;
}

export const MAX_ROW_LABEL = 80;

/** "4010 38 % · 4020 7 %", in the screen's language: how a remembered sales split is said (§3, "shown as applied"). */
export function describeSplit(split: SalesSplit, locale: string): string {
  const pct = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 2 });
  return split.shares.map((s) => `${s.code} ${pct.format(s.percent / 100)}`).join(' · ');
}

/** The day a split was learned, in the screen's language. */
export function splitLearnedOn(split: SalesSplit, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(split.learnedAt));
}
